import { generatePath } from 'react-router-dom';
import { SupportedCurrencies } from 'vopay';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';

// The account page's route: one source of truth for the path, so the <Route> and every link to it
// share one string. Imported as `import * as accountPageRoute` (the `*.route.ts` shape of
// partnerPage.route.ts); `path` is the react-router pattern, `getPath` builds a URL. One page serves
// a top-level account and a sub-account alike, and reads everything about the account from its own
// request.
export const id = 'accountPage';

/**
 * The page's tabs, as their path segment, in spec 5.6 order. `agreement` and `signers` have no
 * trigger: the Details tab and the signer flows route into them.
 */
export const TABS = [
  'details',
  'billing',
  'permissions',
  'webhooks',
  'users',
  'subaccounts',
  'clientAccounts',
  'transactions',
  'agreement',
  'signers',
] as const;

export type AccountPageTab = (typeof TABS)[number];

export const isTab = (value: string | null | undefined): value is AccountPageTab => TABS.some((tab) => tab === value);

export const path = `${ACCOUNTS_PATH}/:accountId/:tab?`;

/** The Webhooks tab keeps its History sub-tab one segment further down; the page renders it as Webhooks. */
export const webhooksHistoryPath = `${ACCOUNTS_PATH}/:accountId/:tab(webhooks)/history`;

/**
 * The link carries only the currency of the row that was clicked, and must carry nothing else: the
 * page embeds shared lists that treat every query parameter as one of their own filters, so a stray
 * `accountStatus=Active` would silently filter a tab. Without a currency the page shows the account's
 * first balance.
 */
export function getPath({
  accountID,
  tab,
  currency,
}: {
  accountID: string;
  tab?: AccountPageTab;
  currency?: SupportedCurrencies;
}) {
  const pathname = generatePath(path, { accountId: accountID, tab });

  return currency ? `${pathname}?${new URLSearchParams({ currency })}` : pathname;
}
